import React from 'react';
import { CornerDownRightIcon } from 'lucide-react';
import { loanAdjustedCalc } from '../../data/loanCommission';
import { negativeCompLoans } from '../../data/periodClose';
import { formatPct, formatUsd } from '../../utils/format';
import { Badge } from '../sf/Badge';
import { NewBadge } from '../sf/NewBadge';
interface Term {
  label: string;
  value: string;
  emphasis?: boolean;
}
function TermBox({
  term
}: {
  term: Term;
}) {
  return <div className={`rounded border px-3 py-1.5 text-center ${term.emphasis ? 'border-sf-green bg-sf-green-light' : 'border-sf-border bg-white'}`} data-id="element-242" data-mp="gr6dwm3bdui3">
      <p className="whitespace-nowrap text-[11px] text-sf-weak" data-id="element-243" data-mp="6t8q1zpd9zq0">{term.label}</p>
      <p className={`whitespace-nowrap tabular-nums ${term.emphasis ? 'text-[15px] font-bold' : 'text-[13px] font-semibold'}`} data-id="element-244" data-mp="2s6o3b42f9kc">{term.value}</p>
    </div>;
}
function Operator({
  symbol
}: {
  symbol: string;
}) {
  return <span className="px-0.5 text-lg font-semibold text-sf-weak" aria-hidden="true" data-id="element-245" data-mp="2ni5vnjh77gmf">
      {symbol}
    </span>;
}
export function AdjustedEquation() {
  const calc = loanAdjustedCalc;
  const negative = negativeCompLoans[0];
  const sequence: (Term | string)[] = [{
    label: 'Loan amount',
    value: formatUsd(calc.loanAmount)
  }, '×', {
    label: 'Total Comp Corr',
    value: formatPct(calc.totalCompPct)
  }, '=', {
    label: 'Comp revenue',
    value: formatUsd(calc.revenue)
  }, '−', {
    label: 'Fee short (halved)',
    value: formatUsd(calc.feeShort)
  }, '=', {
    label: 'Net revenue',
    value: formatUsd(calc.revenueAfterShort)
  }, '÷', {
    label: `Divisor (${calc.divisorRuleKey})`,
    value: formatPct(calc.divisorPct)
  }, '=', {
    label: 'Adjusted Loan Amount',
    value: formatUsd(calc.adjustedAmount),
    emphasis: true
  }];
  const definitions = [{
    term: 'Total Comp Corr %',
    text: `the revenue LendingForce earns on the loan, as a % of the loan amount (here ${formatPct(calc.totalCompPct)} = ${formatUsd(calc.revenue)}).`
  }, {
    term: 'Fee short',
    text: 'when the borrower’s fees don’t cover the target, half of the shortfall is subtracted. Correspondent non-HELOC loans only.'
  }, {
    term: 'Divisor',
    text: 'the standard revenue % for the loan’s comp type and product. Below it, the loan is scaled by comp % ÷ divisor; at or above it, the raw loan amount is used — no gross-up, no cap, no floor.'
  }];
  return <div className="space-y-3" data-id="element-246" data-mp="1iyr0jfm727na">
      <dl className="grid grid-cols-1 gap-x-6 gap-y-1.5 text-[13px] lg:grid-cols-3" data-id="element-247" data-mp="1iyzdf7ibhcyh">
        {definitions.map(item => <div key={item.term} data-id="element-248" data-mp="1e05zxf315lyb">
            <dt className="inline font-semibold" data-id="element-249" data-mp="1dxtid316pdls">{item.term}</dt>
            <dd className="inline text-sf-text" data-id="element-250" data-mp="19whtx0u1qyf6"> = {item.text}</dd>
          </div>)}
      </dl>
      <p className="text-xs text-sf-weak" data-id="element-251" data-mp="14ygdkno09d7e">
        Comp type: <span className="text-sf-text" data-id="element-252" data-mp="3uoter91zsrnz">{calc.compType}</span> · Product: <span className="text-sf-text" data-id="element-253" data-mp="3um795eynwm8e">{calc.product}</span>
      </p>
      <div className="flex flex-wrap items-center gap-1.5" role="img" aria-label={`${formatUsd(calc.revenue)} minus ${formatUsd(calc.feeShort)} equals ${formatUsd(calc.revenueAfterShort)}, divided by ${formatPct(calc.divisorPct)} equals ${formatUsd(calc.adjustedAmount)}`} data-id="element-254" data-mp="3podya8mez41s">
        {sequence.map((item, index) => typeof item === 'string' ? <Operator key={index} symbol={item} data-id="element-255" data-mp="2iz2ywu7qhudg" /> : <TermBox key={index} term={item} data-id="element-256" data-mp="2j048svd0katl" />)}
      </div>
      <div className="space-y-1 text-xs text-sf-weak" data-id="element-257" data-mp="2ny5tfkq38i76">
        <p className="tabular-nums" data-id="element-258" data-mp="2nzqn784kvx3z">
          Fee short = ({formatUsd(calc.threshold)} target − {formatUsd(calc.processingCollected)} processing collected − {formatUsd(calc.underwritingCollected)} underwriting) ÷ 2 ={' '}
          {formatUsd(calc.feeShort)} (= 0.0612% of the loan), so Adjusted Total Comp Corr = {calc.adjustedCompPct.toFixed(4)}%. VA IRRRL target is $555.00.
        </p>
        <p className="tabular-nums" data-id="element-259" data-mp="13jlypcu5610y">
          Adjusted comp = {formatUsd(calc.adjustedAmount)} × 1.50% = <strong className="text-sf-text" data-id="element-260" data-mp="17jo2brjft4iv">$5,785.38</strong> (base $4,821.15 + bonus $964.23) vs stored $6,187.50.
        </p>
        <p className="tabular-nums" data-id="element-261" data-mp="cb28wvhcpx6e">If comp is 3.25% or higher the Adjusted Loan Amount is simply the raw loan amount (e.g. 3.40% → $412,500.00).</p>
      </div>
      <div className="flex items-start gap-2 rounded border border-dashed border-sf-amber bg-sf-amber-light px-3 py-2" data-id="element-262" data-mp="m62k5tevrtdf">
        <CornerDownRightIcon className="mt-0.5 h-4 w-4 shrink-0 text-sf-amber-text" aria-hidden="true" data-id="element-263" data-mp="h67wdyqejjwl" />
        <div className="text-[13px]" data-id="element-264" data-mp="3pm1euj4an5ga">
          <p className="flex flex-wrap items-center gap-2 font-semibold text-sf-amber-text" data-id="element-265" data-mp="3kpj9pi51x53f">
            If net revenue is below $0 <Badge tone="warning" data-id="element-266" data-mp="39kvjkvbw9le">Negative comp</Badge>
          </p>
          <p className="text-xs tabular-nums text-sf-text" data-id="element-267" data-mp="3ungvlyzfupsv">
            Main uses ABS() on the comp %, so it still produces an amount — no floor, no cap. Example {negative.loanNumber}: {formatUsd(negative.revenue)} −{' '}
            {formatUsd(negative.feeShort)} = {formatUsd(negative.revenueAfterShort)} → ABS $202.20 ÷ {formatPct(negative.divisorPct)} ={' '}
            <strong data-id="element-268" data-mp="1heeb6ui0tkwz">{formatUsd(negative.adjustedAmount)}</strong>
          </p>
          <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-sf-weak" data-id="element-269" data-mp="1m8sc1cfgck91">
            <NewBadge label="NEW / requested" data-id="element-270" data-mp="18h3zj81ajtrj" />
            Show $0.00 adjusted and flat $500 LO / $250 TL — not applied.
          </p>
        </div>
      </div>
    </div>;
}